/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import PubNub from "pubnub";
import * as UpduoAnalytics from "config/UpduoAnalytics";

import { store } from "store";

import { RootState } from "store/types";

interface UpduoPubNubInterface {
  subscribedMessages: {
    [key: string]: Function;
  };
  client: null | PubNub;
  uuid: string;
}

export const UpduoPubNub: UpduoPubNubInterface = {
  subscribedMessages: {},
  client: null,
  uuid: "",
};

export function reportPubNubError(type: string, error: any) {
  if (error) {
    UpduoAnalytics.logEvent("app-pubnub-reportPubNubError", { type, error });
  }
}

export function initializePubNub(callback = () => {}) {
  releasePubNubApp(() => {
    const reduxState = store.getState() as unknown as RootState;

    // Open a connection to PubNub
    if (UpduoPubNub.client) {
      UpduoAnalytics.logEvent("app-pubnub-client-exists");
      callback();
    } else {
      UpduoPubNub.client = new PubNub({
        publishKey: process.env.REACT_APP_PUBNUB_PUBLISH_KEY,
        subscribeKey: process.env.REACT_APP_PUBNUB_SUBSCRIBE_KEY || "",
        secretKey: process.env.REACT_APP_PUBNUB_SECRET_KEY,
        uuid: reduxState.selfObject.userId,
        listenToBrowserNetworkEvents: false, // allow SDK reconnection logic to take over
        ssl: true,
        restore: true,
      });

      UpduoPubNub.client.addListener({
        message: function (msg) {
          // handle message
          const messageData = msg.message;
          // PubNub data from Skale server is JSON string; need to parse it
          const parsedMessageData = messageData
            ? JSON.parse(messageData)
            : null;
          const event = parsedMessageData.event;

          if (event in UpduoPubNub.subscribedMessages) {
            UpduoPubNub.subscribedMessages[event](parsedMessageData);
          }
        },
        status: function (statusEvent) {
          const category = statusEvent.category; // Returns PNConnectedCategory when connected
          UpduoAnalytics.logEvent(`app-pubnub-status-${category}`);
          if (category !== "PNConnectedCategory") {
            reportPubNubError("connection", category);
            UpduoPubNub.client = null;
          }
        },
      });

      UpduoPubNub.client.subscribe({
        channels: [reduxState.selfObject.channelName],
      });

      UpduoPubNub.client.subscribe({
        channels: [reduxState.selfObject.organizationChannelName],
        withPresence: true,
      });

      UpduoAnalytics.logEvent("app-pubnub-initializePubNub", {
        channels: [
          reduxState.selfObject.channelName,
          reduxState.selfObject.organizationChannelName,
        ],
      });

      callback();
    }
  });
}

export function releasePubNubApp(callback = () => {}) {
  // End all open requests and close the PubNub instance.
  // Does not clear PubNub subscribedMessages.
  UpduoAnalytics.logEvent("app-pubnub-releasePubNubApp");
  leaveChannel(() => {
    if (UpduoPubNub.client) {
      UpduoPubNub.client.stop();
    }
    UpduoPubNub.client = null;
    UpduoPubNub.uuid = "";
    callback();
  });
}

function leaveChannel(callback = () => {}) {
  const reduxState = store.getState() as any;
  const channels = [
    reduxState.selfObject.channelName,
    reduxState.selfObject.organizationChannelName,
  ];

  if (UpduoPubNub.client) {
    UpduoPubNub.client.unsubscribe({ channels });
  }

  callback();
}

export function addPubNubSubscribedMessage(
  event: string,
  handleFunction: Function
) {
  // Each PubNub channel response that may make use of that channel message data
  UpduoPubNub.subscribedMessages[event] = handleFunction;
}

export function removePubNubSubscribedMessage(event) {
  UpduoAnalytics.logEvent("app-pubnub-removePubNubSubscribedMessage", {
    event,
  });
  delete UpduoPubNub.subscribedMessages[event];
}

export function clearPubNubSubscribedMessages(allowList: string[] = []) {
  // Remove all PubNub subscribed events (except for ones in allowList)
  UpduoPubNub.subscribedMessages = Object.keys(UpduoPubNub.subscribedMessages)
    .filter((key) => allowList.includes(key))
    .reduce((obj: any, key) => {
      obj[key] = UpduoPubNub.subscribedMessages[key];
      return obj;
    }, {});
}

export function recoverPubNubEvent(
  event: string,
  handleFunction: (recentMessages: any) => void
) {
  // TODO: Further DRY refactoring to include timer logic
  if (UpduoPubNub.client) {
    const reduxState = store.getState() as any;
    const channelName = reduxState.selfObject.channelName;
    // Retrieve only the most recent 6 messages
    UpduoPubNub.client.fetchMessages(
      { channels: [channelName], count: 6 },
      (status, response) => {
        if (status.error) {
          reportPubNubError("app-pubnub-history-get", { status });
          return;
        }
        if (!response?.channels?.[channelName]) {
          return;
        }
        // PubNub historical messages are JSON strings; need to parse them into objects
        const recentMessages = response.channels[channelName]
          .map((entry) => JSON.parse(entry?.message))
          .filter((message) => message?.event === event);
        if (recentMessages.length > 0) {
          UpduoAnalytics.logEvent("app-pubnub-recover-event", { event });
          handleFunction(recentMessages);
        }
      }
    );
  }
}
